import { getTheme, setLocalTheme } from "@/lib/storeManager";
import { darkTheme } from "@/utils/themes/darkTheme";
import { lightTheme } from "@/utils/themes/lightTheme";
import { ThemeConfig } from "antd";
import { createContext, ReactNode, useEffect, useState } from "react";

type ThemeType = "light" | "dark";

const ThemeContext = createContext<{
  theme: ThemeConfig;
  current: ThemeType;
  toggleTheme: () => void;
}>({
  theme: lightTheme,
  current: "light",
  toggleTheme: () => {},
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [current, setCurrent] = useState<ThemeType>("light");

  const toggleTheme = () => {
    setLocalTheme(current === "light" ? "dark" : "light");
    setCurrent(prev => (prev === "light" ? "dark" : "light"));
  };

  // Load saved theme from localStorage on mount
  useEffect(() => {
    const savedTheme = getTheme();
    if (savedTheme === "light" || savedTheme === "dark") {
      setCurrent(savedTheme);
    }
  }, []);

  // Sync theme to DOM for Tailwind dark mode (.dark class) and CSS variables
  useEffect(() => {
    if (current === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [current]);

  const selectedTheme = current === "light" ? lightTheme : darkTheme;

  return (
    <ThemeContext.Provider
      value={{ theme: selectedTheme, current, toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// Export the context for use in other files
export { ThemeContext };
